import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { caseStudies } from '../content/blog/caseStudies';
import './CaseStudyDetailScreen.css';

const CaseStudyDetailScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const study = caseStudies.find(s => s.id === id);

  if (!study) {
    return (
      <div className="case-study-detail-screen">
        <div className="container">
          <h1>Case Study Not Found</h1>
          <p>The case study you're looking for doesn't exist.</p>
          <Link to="/blog">Back to Blog</Link>
        </div>
      </div>
    );
  }

  // Find related case studies (same sport or level)
  const relatedStudies = caseStudies
    .filter(s => s.id !== study.id && (
      s.sport === study.sport || 
      s.userLevel === study.userLevel
    ))
    .slice(0, 2);

  return (
    <div className="case-study-detail-screen" style={{ backgroundColor: theme.colors.background }}>
      <div className="container">
        <button
          onClick={() => navigate('/blog')}
          className="back-button"
          style={{
            color: theme.colors.primary,
            borderColor: theme.colors.primary
          }}
        >
          ← Back to Blog
        </button>

        <article className="case-study-detail" style={{ backgroundColor: theme.colors.surface }}>
          <header className="case-study-header">
            {study.featured && (
              <span 
                className="featured-badge"
                style={{ backgroundColor: theme.colors.primary }}
              >
                Featured
              </span>
            )}
            <h1 style={{ color: theme.colors.text }}>{study.title}</h1>
            <p className="case-study-excerpt-large" style={{ color: theme.colors.textSecondary }}>
              {study.excerpt}
            </p>
            <div className="case-study-meta-detail">
              <span 
                className="sport-badge-large"
                style={{
                  backgroundColor: theme.colors.primary + '20',
                  color: theme.colors.primary
                }}
              >
                {study.sport}
              </span>
              <span style={{ color: theme.colors.textSecondary }}>
                Level: {study.userLevel}
              </span>
              <span style={{ color: theme.colors.textSecondary }}>
                Duration: {study.duration}
              </span>
              <span style={{ color: theme.colors.textSecondary }}>
                {new Date(study.date).toLocaleDateString()}
              </span>
            </div>
          </header>

          <div 
            className="case-study-content"
            style={{ color: theme.colors.text }}
          >
            {study.content.split('\n\n').map((paragraph, index) => (
              paragraph.trim() && (
                <p key={index}>
                  {paragraph.split('\n').map((line, lineIndex) => (
                    <React.Fragment key={lineIndex}>
                      {lineIndex > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </p>
              )
            ))}
          </div>

          {study.outcomes && study.outcomes.length > 0 && (
            <section className="case-study-outcomes">
              <h2 style={{ color: theme.colors.text }}>Key Outcomes</h2>
              <ul className="outcomes-list">
                {study.outcomes.map((outcome, index) => (
                  <li key={index} style={{ color: theme.colors.textSecondary }}>
                    {outcome}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {study.metrics && study.metrics.length > 0 && (
            <section className="case-study-metrics">
              <h2 style={{ color: theme.colors.text }}>Performance Metrics</h2>
              <div className="metrics-grid">
                {study.metrics.map((metric, index) => (
                  <div 
                    key={index}
                    className="metric-card"
                    style={{
                      backgroundColor: theme.colors.background,
                      borderColor: theme.colors.primary + '40'
                    }}
                  >
                    <div className="metric-label" style={{ color: theme.colors.textSecondary }}>
                      {metric.label}
                    </div>
                    <div className="metric-values">
                      <span className="metric-before" style={{ color: theme.colors.textSecondary }}>
                        {metric.before}
                      </span>
                      <span className="metric-arrow" style={{ color: theme.colors.primary }}>
                        →
                      </span>
                      <span className="metric-after" style={{ color: theme.colors.primary, fontWeight: 'bold' }}>
                        {metric.after}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <footer className="case-study-footer">
            <div className="case-study-share">
              <h4 style={{ color: theme.colors.text }}>Share this case study:</h4>
              <div className="share-buttons">
                <button
                  onClick={() => {
                    navigator.share?.({
                      title: study.title,
                      text: study.excerpt,
                      url: window.location.href
                    }).catch(() => {});
                  }}
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: '#fff'
                  }}
                >
                  Share
                </button>
              </div>
            </div>
          </footer>
        </article>

        {relatedStudies.length > 0 && (
          <section className="related-case-studies">
            <h2 style={{ color: theme.colors.text }}>Related Case Studies</h2>
            <div className="related-case-studies-grid">
              {relatedStudies.map((related) => (
                <Link
                  key={related.id}
                  to={`/blog/case-study/${related.id}`}
                  className="related-case-study-card"
                  style={{ backgroundColor: theme.colors.surface }}
                >
                  <h3 style={{ color: theme.colors.text }}>{related.title}</h3>
                  <p style={{ color: theme.colors.textSecondary }}>
                    {related.excerpt.substring(0, 100)}...
                  </p>
                  <span style={{ color: theme.colors.primary }}>
                    Read more →
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default CaseStudyDetailScreen;
